import { PasswordValidationResult } from "../types/registrationTypes";

/**
 * Password validation utilities
 * Based on the backend requirements from the API specification
 */

/**
 * Validates password based on user role requirements
 * @param password - The password to validate
 * @param userRole - The user's role (affects requirements)
 * @returns PasswordValidationResult with validation status and errors
 */
export const validatePassword = (
  password: string,
  userRole: string = "user"
): PasswordValidationResult => {
  const errors: string[] = [];
  
  // Determine minimum length based on role
  const minLength = ["admin", "manager"].includes(userRole) ? 12 : 8;
  
  // Length validation
  if (password.length < minLength) {
    errors.push(`Password must be at least ${minLength} characters long`);
  }
  
  // Additional requirements for admin/manager roles
  if (["admin", "manager"].includes(userRole)) {
    if (!/[A-Z]/.test(password)) {
      errors.push("Must contain at least one uppercase letter");
    }
    if (!/[a-z]/.test(password)) {
      errors.push("Must contain at least one lowercase letter");
    }
    if (!/\d/.test(password)) {
      errors.push("Must contain at least one number");
    }
  }
  
  // Calculate password strength
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  let strengthScore = 0;
  
  if (password.length >= 8) strengthScore++;
  if (password.length >= 12) strengthScore++;
  if (/[A-Z]/.test(password)) strengthScore++;
  if (/[a-z]/.test(password)) strengthScore++;
  if (/\d/.test(password)) strengthScore++;
  if (/[^A-Za-z0-9]/.test(password)) strengthScore++; // Special characters
  
  if (strengthScore >= 5) strength = 'strong';
  else if (strengthScore >= 3) strength = 'medium';
  
  return {
    valid: errors.length === 0,
    errors,
    strength
  };
};

/**
 * Validates that two passwords match
 * @param password - The original password
 * @param confirmPassword - The confirmation password
 * @returns boolean indicating if passwords match
 */
export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): boolean => {
  return password === confirmPassword;
};

/**
 * Gets a user-friendly strength label with color
 * @param strength - The password strength level
 * @returns Object with label and color for UI display
 */
export const getPasswordStrengthDisplay = (strength: 'weak' | 'medium' | 'strong') => {
  switch (strength) {
    case 'weak':
      return { label: 'Weak', color: '#f44336' }; // Red
    case 'medium':
      return { label: 'Medium', color: '#ff9800' }; // Orange
    case 'strong':
      return { label: 'Strong', color: '#4caf50' }; // Green
    default:
      return { label: 'Weak', color: '#f44336' };
  }
};

/**
 * Validates email domain for @centese.com requirement
 * @param email - The email address to validate
 * @returns boolean indicating if domain is allowed
 */
export const validateEmailDomain = (email: string): boolean => {
  const allowedDomain = '@centese.com';
  return email.toLowerCase().endsWith(allowedDomain.toLowerCase());
};

/**
 * Gets a user-friendly error message for email domain validation
 * @param email - The email that failed validation
 * @returns string with error message
 */
export const getEmailDomainError = (email: string): string => {
  return "Sorry, only @centese.com email addresses are allowed for registration.";
};